/* Admin dialog: add, rename, reorder and delete report categories
 * (MOP, MOS, ...). The order here is the order of the groups on the TV
 * Reports menu. Deleting a category keeps its reports -- they move to
 * Uncategorized. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Tags, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { adminApi } from "../../api/admin";
import { ApiError } from "../../api/client";
import { Banner, Button, EmptyState, IconButton, Input, Modal, SkeletonRows, useConfirm, useToast } from "../../ds";
import type { ReportCategory } from "../../types";
import { CATEGORIES_KEY, sortCategories } from "./reportCategories";

function errorText(e: unknown, name?: string): string {
  if (e instanceof ApiError && e.status === 409) return `There's already a category called “${name ?? ""}”. Pick another name.`;
  return e instanceof Error ? e.message : "That didn't work.";
}

export function ManageCategoriesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const list = useQuery({ queryKey: CATEGORIES_KEY, queryFn: adminApi.listReportCategories, enabled: open, retry: false });
  const cats = sortCategories(list.data ?? []);

  const [newName, setNewName] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNewName("");
    setAddError(null);
    setEditId(null);
    setEditError(null);
  }, [open]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: CATEGORIES_KEY });
    // Report cards/workspace carry category_name, so they change too.
    qc.invalidateQueries({ queryKey: ["reports"] });
    qc.invalidateQueries({ queryKey: ["report"] });
  };
  const taken = (name: string, exceptId?: string) => cats.some((c) => c.id !== exceptId && c.name.trim().toLowerCase() === name.trim().toLowerCase());

  const add = useMutation({
    mutationFn: (name: string) => adminApi.createReportCategory({ name, sort_order: (cats.length ? Math.max(...cats.map((c) => c.sort_order)) : 0) + 1 }),
    onSuccess: (c) => {
      setNewName("");
      setAddError(null);
      toast.success(`Category “${c.name}” added`, "Pick it on any report to show that report under it on the TV.");
      refresh();
    },
    onError: (e, name) => setAddError(errorText(e, name)),
  });
  const submitAdd = () => {
    const name = newName.trim();
    if (!name) return setAddError("Type a name first, for example MOP.");
    if (taken(name)) return setAddError(`There's already a category called “${name}”.`);
    add.mutate(name);
  };

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => adminApi.updateReportCategory(id, { name }),
    onSuccess: (c) => {
      setEditId(null);
      setEditError(null);
      toast.success("Category renamed", `Its reports now appear under “${c.name}”.`);
      refresh();
    },
    onError: (e, v) => setEditError(errorText(e, v.name)),
  });
  const submitRename = (c: ReportCategory) => {
    const name = editName.trim();
    if (!name) return setEditError("The name can't be empty.");
    if (name === c.name) return setEditId(null);
    if (taken(name, c.id)) return setEditError(`There's already a category called “${name}”.`);
    rename.mutate({ id: c.id, name });
  };

  const move = useMutation({
    mutationFn: async ({ from, to }: { from: number; to: number }) => {
      const next = cats.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      // Renumber 1..n so the order is unambiguous; only send what changed.
      const changed = next.map((c, i) => ({ c, order: i + 1 })).filter(({ c, order }) => c.sort_order !== order);
      qc.setQueryData<ReportCategory[]>(CATEGORIES_KEY, next.map((c, i) => ({ ...c, sort_order: i + 1 })));
      for (const { c, order } of changed) await adminApi.updateReportCategory(c.id, { sort_order: order });
    },
    onError: (e) => toast.error("Couldn't change the order", e instanceof Error ? e.message : undefined),
    onSettled: () => qc.invalidateQueries({ queryKey: CATEGORIES_KEY }),
  });

  const remove = useMutation({
    mutationFn: (c: ReportCategory) => adminApi.deleteReportCategory(c.id),
    onSuccess: (_d, c) => {
      toast.success(`Category “${c.name}” deleted`, c.report_count ? `${c.report_count} report${c.report_count === 1 ? "" : "s"} moved to Uncategorized.` : undefined);
      refresh();
    },
    onError: (e) => toast.error("Couldn't delete the category", e instanceof Error ? e.message : undefined),
  });
  const askRemove = async (c: ReportCategory) => {
    const n = c.report_count;
    const ok = await confirm({
      title: `Delete the “${c.name}” category?`,
      message: n
        ? `Its ${n} report${n === 1 ? "" : "s"} move to Uncategorized — the reports themselves aren't changed or deleted, and you can put them in another category any time.`
        : "No reports are in it, so nothing else changes.",
      confirmLabel: "Delete category",
      tone: "danger",
    });
    if (ok) remove.mutate(c);
  };

  const busy = move.isPending || remove.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Report categories"
      description="Each report belongs to one company category. On the TV Reports menu, reports are grouped under these, in this order."
      footer={<Button variant="primary" onClick={onClose}>Done</Button>}
    >
      {list.isLoading ? (
        <SkeletonRows rows={3} />
      ) : list.isError ? (
        <Banner tone="danger" title="Couldn't load the categories">
          {list.error instanceof ApiError && list.error.status === 404 ? "This server doesn't support categories yet — it needs the latest platform update." : list.error instanceof Error ? list.error.message : null}
        </Banner>
      ) : (
        <div className="ds-stack" style={{ gap: 16 }}>
          {cats.length === 0 ? (
            <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 10 }}>
              <EmptyState icon={Tags} title="No categories yet" description="Add one below, for example MOP or MOS." />
            </div>
          ) : (
            <div className="ds-list" style={{ border: "1px solid var(--border)", borderRadius: 10 }} role="list" aria-label="Categories in TV menu order">
              {cats.map((c, i) => {
                const editing = editId === c.id;
                return (
                  <div key={c.id} className="ds-list__row" role="listitem" style={{ padding: "10px 12px 10px 16px", alignItems: editing && editError ? "flex-start" : "center" }}>
                    <span className="ds-small ds-faint" style={{ width: 18, textAlign: "right", fontVariantNumeric: "tabular-nums", paddingTop: editing && editError ? 9 : 0 }}>{i + 1}</span>
                    {editing ? (
                      <div className="ds-grow" style={{ minWidth: 0 }}>
                        <Input
                          inputSize="sm"
                          value={editName}
                          autoFocus
                          aria-label={`New name for ${c.name}`}
                          onChange={(e) => { setEditName(e.target.value); setEditError(null); }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") submitRename(c);
                            if (e.key === "Escape") { e.stopPropagation(); setEditId(null); }
                          }}
                        />
                        {editError ? <div className="ds-error-text" style={{ marginTop: 4 }}>{editError}</div> : null}
                      </div>
                    ) : (
                      <div className="ds-list__main">
                        <div className="ds-list__title ds-truncate">{c.name}</div>
                        <div className="ds-list__meta">{c.report_count === 0 ? "No reports yet" : `${c.report_count} report${c.report_count === 1 ? "" : "s"}`}</div>
                      </div>
                    )}
                    {editing ? (
                      <>
                        <Button size="sm" variant="primary" icon={Check} loading={rename.isPending} onClick={() => submitRename(c)}>Save</Button>
                        <IconButton size="sm" icon={X} label="Cancel rename" onClick={() => { setEditId(null); setEditError(null); }} />
                      </>
                    ) : (
                      <span className="ds-row" style={{ gap: 2 }}>
                        <IconButton size="sm" icon={ArrowUp} label={`Move ${c.name} up`} disabled={i === 0 || busy} onClick={() => move.mutate({ from: i, to: i - 1 })} />
                        <IconButton size="sm" icon={ArrowDown} label={`Move ${c.name} down`} disabled={i === cats.length - 1 || busy} onClick={() => move.mutate({ from: i, to: i + 1 })} />
                        <IconButton size="sm" icon={Pencil} label={`Rename ${c.name}`} disabled={busy} onClick={() => { setEditId(c.id); setEditName(c.name); setEditError(null); }} />
                        <IconButton size="sm" icon={Trash2} label={`Delete ${c.name}`} disabled={busy} onClick={() => askRemove(c)} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <div className="ds-label" style={{ marginBottom: 6 }}>Add a category</div>
            <div className="ds-row" style={{ gap: 8, alignItems: "flex-start" }}>
              <div className="ds-grow">
                <Input
                  value={newName}
                  placeholder="e.g. MOP"
                  aria-label="New category name"
                  maxLength={80}
                  onChange={(e) => { setNewName(e.target.value); setAddError(null); }}
                  onKeyDown={(e) => e.key === "Enter" && submitAdd()}
                />
                {addError ? <div className="ds-error-text" style={{ marginTop: 4 }}>{addError}</div> : <div className="ds-hint" style={{ marginTop: 4 }}>Short names read best on the TV — usually the company, like MOP or MOS.</div>}
              </div>
              <Button icon={Plus} loading={add.isPending} onClick={submitAdd}>Add</Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
